import { useEffect } from "react";

/**
 * One hook to power the entire site's motion language.
 *
 * Drop-in attributes (no per-page wiring required):
 *
 *   [data-kinetic]        On an H1/H2 → split into words, stagger fade-up
 *                         when the element scrolls into view.
 *
 *   [data-magnetic]       On a button/link → subtle cursor-follow translate
 *                         on hover (max ~6px). No-op for touch devices.
 *
 *   [data-count="123"]    On any element → animate from 0 → target value
 *                         when the element scrolls into view. Pulls
 *                         `data-count-prefix` and `data-count-suffix` for
 *                         decoration (e.g. "$" + "+").
 *
 * Respects `prefers-reduced-motion`. Cleans up listeners + observers on unmount.
 */
export default function useSiteMotion() {
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // ---------- Kinetic headlines ----------
    const headings = Array.from(document.querySelectorAll("[data-kinetic]"));
    headings.forEach((el) => {
      if (el.dataset.kineticReady === "1") return;
      el.dataset.kineticReady = "1";
      // Walk childNodes so inline styling (italic spans, accents) is preserved.
      // - Text nodes are split into per-word spans.
      // - Element children are kept whole and treated as one animation token.
      let idx = 0;
      const tokens = [];
      Array.from(el.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const parts = (node.textContent || "").split(/(\s+)/);
          parts.forEach((p) => {
            if (p === "") return;
            if (/^\s+$/.test(p)) {
              tokens.push(document.createTextNode(p));
              return;
            }
            const span = document.createElement("span");
            span.className = "kinetic-word";
            span.style.animationDelay = `${Math.min(idx * 60, 700)}ms`;
            span.textContent = p;
            tokens.push(span);
            idx += 1;
          });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          node.classList.add("kinetic-word");
          node.style.animationDelay = `${Math.min(idx * 60, 700)}ms`;
          tokens.push(node);
          idx += 1;
        }
      });
      // Safely clear children without parsing untrusted HTML. `innerHTML = ""`
      // assigns a constant string (no XSS surface), but using replaceChildren()
      // is the modern, lint-clean equivalent and avoids touching the HTML
      // parser at all.
      el.replaceChildren();
      tokens.forEach((t) => el.appendChild(t));
    });

    if (reduce) return undefined;

    // Trigger kinetic animation when heading enters viewport
    const kineticIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("kinetic-in");
            kineticIo.unobserve(e.target);
          }
        });
      },
      { threshold: 0.2 },
    );
    headings.forEach((el) => kineticIo.observe(el));

    // ---------- Magnetic buttons ----------
    const isTouch =
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse)").matches;
    const magnetics = Array.from(document.querySelectorAll("[data-magnetic]"));
    const magnetCleanup = [];
    if (!isTouch) {
      magnetics.forEach((el) => {
        const onMove = (ev) => {
          const r = el.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
          const dx = (ev.clientX - cx) / (r.width / 2);
          const dy = (ev.clientY - cy) / (r.height / 2);
          const max = 6;
          el.style.transform = `translate(${(dx * max).toFixed(2)}px, ${(dy * max).toFixed(2)}px)`;
        };
        const onLeave = () => {
          el.style.transform = "";
        };
        el.addEventListener("mousemove", onMove);
        el.addEventListener("mouseleave", onLeave);
        magnetCleanup.push(() => {
          el.removeEventListener("mousemove", onMove);
          el.removeEventListener("mouseleave", onLeave);
          el.style.transform = "";
        });
      });
    }

    // ---------- Count-up numbers ----------
    const counters = Array.from(document.querySelectorAll("[data-count]"));
    const countIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const el = e.target;
          if (el.dataset.countDone === "1") return;
          el.dataset.countDone = "1";
          const target = Number(el.dataset.count) || 0;
          const prefix = el.dataset.countPrefix || "";
          const suffix = el.dataset.countSuffix || "";
          const isCurrency = el.dataset.countFormat === "currency";
          const dur = 1100;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / dur);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - t, 3);
            const v = Math.round(target * eased);
            const formatted = isCurrency
              ? v.toLocaleString("en-US")
              : String(v);
            el.textContent = `${prefix}${formatted}${suffix}`;
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          countIo.unobserve(el);
        });
      },
      { threshold: 0.4 },
    );
    counters.forEach((el) => {
      // Initial render: zero so the count-up is dramatic
      const prefix = el.dataset.countPrefix || "";
      const suffix = el.dataset.countSuffix || "";
      el.textContent = `${prefix}0${suffix}`;
      countIo.observe(el);
    });

    return () => {
      kineticIo.disconnect();
      countIo.disconnect();
      magnetCleanup.forEach((fn) => fn());
    };
     
  });
}
